/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { navigate } from "gatsby";

import classNames from "classnames";
import posthog from "posthog-js";

import { gaBeginCheckout } from "@analytics/google";
import {
  closeCart,
  selectCartItems,
  selectIsCartOpen,
  selectOldTotalPrice,
  selectTotalPrice,
} from "@store/cart";

import ProgressBar from "@components/common/ProgressBar";
import { Button } from "@components/common";

import getUpsellItems from "@helpers/items/getUpsellItems";
import getFreeGifts from "@helpers/items/getFreeGifts";

import PaymentMethodsGallery from "@components/common/image/gallery/payment-methods/PaymentMethodsGallery";
import CartItem from "./item/CartItem";
import UpsellCartItem from "./UpsellCartItem/UpsellCartItem";

const Cart = () => {
  const dispatch = useDispatch();

  const isCartOpen = useSelector(selectIsCartOpen);
  const cartItems = useSelector(selectCartItems);
  const totalPrice = useSelector(selectTotalPrice);
  const oldTotalPrice = useSelector(selectOldTotalPrice);

  const freeGifts = useMemo(() => getFreeGifts(), []);
  const upsellItems = useMemo(
    () => getUpsellItems(cartItems.map((ci) => ci.item.id)).slice(0, 2),
    [cartItems]
  );

  const stepPrices = useMemo(
    () =>
      [40, ...freeGifts.map((fg) => fg.additionalDetails.from).filter(Boolean)].sort() as number[],
    [freeGifts]
  );

  const hasSubscriptionProduct = useMemo(() => {
    return cartItems.some((ci) => ci.configuration.offer.name === "Monthly Subscription");
  }, [cartItems]);

  const [currentStep, setCurrentStep] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.zE === "function") {
      if (isCartOpen) {
        window.zE("messenger", "hide");
      } else {
        window.zE("messenger", "show");
      }
    }
  }, [isCartOpen]);

  useEffect(() => {
    let newStep = 0;
    let remaining = 0;

    if (hasSubscriptionProduct) {
      newStep = 1;
    }

    for (let i = 0; i < stepPrices.length; i++) {
      const stepPrice = stepPrices[i];

      if (stepPrice > totalPrice) {
        remaining = stepPrice - totalPrice;
        break;
      }

      newStep = i + 1;
    }

    setCurrentStep(newStep);
    setRemainingAmount(remaining);
  }, [totalPrice, hasSubscriptionProduct, stepPrices]);

  const handleCloseCart = useCallback(() => {
    dispatch(closeCart());
  }, [dispatch]);

  const handleProceedToCheckout = () => {
    dispatch(closeCart());
    gaBeginCheckout({ value: totalPrice, cartItems });

    if (typeof window !== "undefined") {
      posthog.capture("Start checkout", {
        cartItems,
      });
    }

    void navigate("/checkout");
  };

  useEffect(() => {
    document.addEventListener("click", handleCloseCart);

    return () => document.removeEventListener("click", handleCloseCart);
  }, [handleCloseCart]);

  useEffect(() => {
    if (isCartOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => document.body.classList.remove("modal-open");
  }, [isCartOpen]);

  return (
    <React.Fragment>
      {isCartOpen && <div className="overlay" />}
      <div
        onClick={(e) => e.stopPropagation()}
        className={classNames("cart", { "--is-open": isCartOpen })}
      >
        <div className="cart-header">
          <h3 className="cart-header__title">Shopping Cart</h3>

          <Button
            className="close-btn"
            icon="cross"
            color="link-secondary"
            iconClassName="close-btn-icon"
            aria-label="close-cart"
            onClick={handleCloseCart}
          />
        </div>

        <div className="cart-body">
          <div className="cart-body-content">
            {cartItems.length > 0 && (
              <ProgressBar
                currentStep={currentStep}
                remainingAmount={+remainingAmount.toFixed(2)}
              />
            )}

            {cartItems
              .slice()
              .sort(
                (a, b) => (a.configuration.price === 0 ? 1 : 0) - (b.configuration.price ? 1 : 0)
              )
              .map((item, index) => (
                <CartItem key={index} cartItem={item} />
              ))}
          </div>
          {upsellItems.length && cartItems.length ? (
            <div className="cart-body-upsell">
              <h3>You may also be interested in</h3>
              <div
                className={`cart-body-upsell-items upsell-scroll ${
                  upsellItems.length === 1 && "center"
                }`}
              >
                {upsellItems.map((item) => (
                  <UpsellCartItem key={item.id} item={item} />
                ))}
              </div>
            </div>
          ) : (
            ""
          )}
        </div>

        <div className="cart-footer">
          <div className="cart-footer__total">
            <span className="cart-footer__total-text">TOTAL:</span>
            <div>
              <span className="cart-footer__total-old-price">${oldTotalPrice}</span>
              <span className="cart-footer__total-price">${totalPrice}</span>
            </div>
          </div>

          <Button disabled={cartItems.length === 0} onClick={handleProceedToCheckout}>
            Proceed To Checkout
          </Button>
          <div className="cart-footer__payment-methods">
            <PaymentMethodsGallery className="cart-payment" />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Cart;
